import i18nFactory from '@yandex-int/i18n';
import { isEqual } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { PredictorHelpers } from '../../service/PredictorHelpers/PredictorHelpers';
import { GroupType } from '../../typings/domain';
import { GdPlatformCorrectionsWebInput } from '../../typings/graphql-grid';
import { ReachResultValue, TextPredictor } from '../../typings/predictor';
import { Predictor } from '../Predictor/Predictor';
import { Shape2, Shape2Icon, Shape2Theme, Shape2Tone } from '../Shape2/Shape2';

import './PredictorExpander.css';
import * as keyset from './PredictorExpander.i18n';

export interface PredictorExpanderProps {
    text?: TextPredictor[];
    id?: string;
    isLoading?: boolean;
    sum?: ReachResultValue;
    detailed?: ReachResultValue;
    platform?: GdPlatformCorrectionsWebInput;
    error?: string;
    isGrowPerformanceEnabled?: boolean;
    otsBasic?: ReachResultValue;
    otsDetailed?: ReachResultValue;
    isOutdoor?: boolean;

    updatePredictor: () => void;
    onClose?: () => void;
    onOpen?: () => void;
}

export interface PredictorExpanderState {
    isExpanderOpened: boolean;
}

const i18n = i18nFactory(keyset);

const BLOCK = 'predictor-expander';

export class PredictorExpander extends React.Component<
    PredictorExpanderProps,
    PredictorExpanderState
> {
    state = {
        isExpanderOpened: false
    };

    componentDidUpdate(prevProps: PredictorExpanderProps) {
        if (!isEqual(this.props.text, prevProps.text)) {
            this.update();
        }
    }

    render() {
        const { isExpanderOpened } = this.state;

        return (
            <div className={BLOCK}>
                {isExpanderOpened ? this.getFullView() : this.getSimpleView()}
                {this.getExpanderControl()}
            </div>
        );
    }

    private getFullView = () => {
        const {
            sum,
            detailed,
            text,
            id,
            error,
            otsBasic,
            otsDetailed,
            isOutdoor,
            isLoading
        } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'view_full')}>
                <div className={Bem.cls(BLOCK, 'view_full_help')}>
                    {i18n('predictor-seven-days')}
                </div>
                <Predictor
                    isExpanderOpened={false}
                    isGrowPerformanceEnabled={false}
                    sum={sum}
                    detailed={detailed}
                    text={text}
                    id={id}
                    error={error}
                    cls={BLOCK}
                    otsBasic={otsBasic}
                    otsDetailed={otsDetailed}
                    groupType={
                        isOutdoor ? GroupType.CPM_OUTDOOR : GroupType.CPM_INDOOR
                    }
                    isLoading={isLoading}
                    copyText={i18n('copy-link')}
                    hideTitle={true}
                />
            </div>
        );
    };

    private getSimpleView = () => {
        const { isLoading, sum, detailed } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'view_simple')}>
                <div className={Bem.cls(BLOCK, 'content')}>
                    <div className={Bem.cls(BLOCK, 'numbers')}>
                        {this.getReach()}
                    </div>
                    <div
                        className={Bem.cls({
                            block: BLOCK,
                            elem: 'progress',
                            mods: {
                                loading: isLoading ? 'yes' : 'no'
                            }
                        })}
                    >
                        <div
                            style={{
                                width: `${
                                    isLoading
                                        ? 100
                                        : PredictorHelpers.getPercent(
                                              sum,
                                              detailed
                                          )
                                }%`
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    private getReach = () => {
        const { detailed, sum, error, isOutdoor } = this.props;

        if (!error) {
            return (
                <>
                    <div className={Bem.cls(BLOCK, 'detailed')}>
                        <span>{isOutdoor ? i18n('reach') : i18n('ots')}</span>
                        <div className={Bem.cls(BLOCK, 'value')}>
                            {PredictorHelpers.getDetailedText(detailed)}
                        </div>
                    </div>
                    <div className={Bem.cls(BLOCK, 'basic')}>
                        {PredictorHelpers.getBasedText(sum)}
                    </div>
                </>
            );
        }

        return i18n('predictor-error');
    };

    private update = () => {
        const { updatePredictor } = this.props;

        updatePredictor();
    };

    private getExpanderControl = () => {
        const { isExpanderOpened } = this.state;

        return (
            <Shape2
                cls={Bem.cls({
                    block: BLOCK,
                    elem: 'control-element',
                    mods: {
                        opened: isExpanderOpened ? 'yes' : 'no'
                    }
                })}
                hoverable={true}
                theme={
                    isExpanderOpened ? Shape2Theme.light : Shape2Theme.default
                }
                tone={isExpanderOpened ? Shape2Tone.dark : Shape2Tone.white}
                icon={Shape2Icon.verticalArrow}
                width={14}
                height={14}
                onClick={this.changeStatusExpander}
            />
        );
    };

    private changeStatusExpander = () => {
        this.setState({
            isExpanderOpened: !this.state.isExpanderOpened
        });
    };
}
